/*!custom-currency.js

  Script: Custom Currency Converter
  Descrizione: Questo script aggiunge un pulsante flottante vicino al prezzo del prodotto
  che apre una finestra modale con la conversione del prezzo (EUR -> altre valute).
  Recupera i tassi da endpoint esterni con retry e fallback, effettua caching con TTL,
  osserva modifiche DOM, visibilità e resize per posizionare correttamente il pulsante.

  Funzioni esposte su window.__cc_currency:
  - forceReposition(): forza il ricalcolo e riposizionamento del pulsante.
  - cleanup(): rimuove tutti i listener, osservatori e elementi creati dallo script.
  - invalidateContainerRect(): invalida la cache del bounding rect del container.
*/

(function () {
  'use strict';

  const doc = document;
  const win = window;

  // Configurazioni di default
  const DEFAULTS = {
    PRICE_SELECTOR: 'span.money',
    GENERIC_PRICE_CONTAINER_SELECTOR: '[id*="ProductPrice"]',
    BTN_Z_INDEX: 49,
    RATE_TTL_MS: 300000,
    OFFSET_X: 20,
    CURRENCIES: ['USD', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'EUR'],
    BACKDROP_ID: 'cc-backdrop',
    MODAL_ID: 'cc-modal',
    STYLE_ID: 'cc-style',
    BTN_ID: 'cc-floating-conv-btn',
    RATE_ENDPOINTS: ['https://open.er-api.com/v6/latest/EUR', 'https://api.exchangerate.host/latest?base=EUR'],
    MIN_RESIZE_INTERVAL: 120,
    CONTAINER_RECT_CACHE_TTL: 500
  };

  // Configurazione utente (opzionale)
  const userCfg = (win.__cc_config && typeof win.__cc_config === 'object') ? win.__cc_config : {};
  if (userCfg.enabled === false) return;

  const PRICE_SELECTOR = userCfg.priceSelector || DEFAULTS.PRICE_SELECTOR;
  const GENERIC_PRICE_CONTAINER_SELECTOR = userCfg.priceContainerSelector || DEFAULTS.GENERIC_PRICE_CONTAINER_SELECTOR;

  const CONFIG = {
    BTN_Z_INDEX: userCfg.btnZIndex || DEFAULTS.BTN_Z_INDEX,
    RATE_TTL_MS: userCfg.rateTtlMs || DEFAULTS.RATE_TTL_MS,
    OFFSET_X: (typeof userCfg.offsetX === 'number') ? userCfg.offsetX : DEFAULTS.OFFSET_X,
    CURRENCIES: Array.isArray(userCfg.currencies) ? userCfg.currencies : DEFAULTS.CURRENCIES,
    BACKDROP_ID: userCfg.backdropId || DEFAULTS.BACKDROP_ID,
    MODAL_ID: userCfg.modalId || DEFAULTS.MODAL_ID,
    STYLE_ID: userCfg.styleId || DEFAULTS.STYLE_ID,
    BTN_ID: userCfg.btnId || DEFAULTS.BTN_ID,
    RATE_ENDPOINTS: Array.isArray(userCfg.rateEndpoints) ? userCfg.rateEndpoints : DEFAULTS.RATE_ENDPOINTS,
    MIN_RESIZE_INTERVAL: userCfg.minResizeInterval || DEFAULTS.MIN_RESIZE_INTERVAL,
    CONTAINER_RECT_CACHE_TTL: userCfg.containerRectCacheTtl || DEFAULTS.CONTAINER_RECT_CACHE_TTL
  };

  // Traduzioni per UI
  const TRANSLATIONS = {
    it: {
      trigger: 'Prezzo in altre valute',
      title: 'Prezzo in altre valute',
      subtitle: 'I tassi mostrati si riferiscono al cambio attuale e possono subire variazioni.',
      loading: 'Caricamento tassi…',
      note: 'Prezzi indicativi. Addebito in EUR al checkout.',
      close: 'Chiudi',
      noConversions: 'Conversioni non disponibili',
      ratesUnavailable: 'Tassi non disponibili'
    },
    en: {
      trigger: 'Price in other currencies',
      title: 'Price in other currencies',
      subtitle: 'Displayed rates reflect current exchange and may change over time.',
      loading: 'Loading rates…',
      note: 'Indicative prices. Charged in EUR at EUR checkout.',
      close: 'Close',
      noConversions: 'Conversions not available',
      ratesUnavailable: 'Rates unavailable'
    },
    fr: {
      trigger: "Prix dans d'autres devises",
      title: "Prix dans d'autres devises",
      subtitle: 'Les taux affichés reflètent le cours actuel et peuvent varier.',
      loading: 'Chargement des taux…',
      note: 'Prix indicatifs. Facturation in EUR lors du paiement.',
      close: 'Fermer',
      noConversions: 'Conversiones non disponibili',
      ratesUnavailable: 'Taux non disponibles'
    },
    de: {
      trigger: 'Preis in anderen Währungen',
      title: 'Preis in anderen Währungen',
      subtitle: 'Angezeigte Kurse entsprechen del cambio attuale e possono cambiare.',
      loading: 'Lade Kurse…',
      note: 'Unverbindliche Preise. Abrechnung in EUR beim Checkout.',
      close: 'Schließen',
      noConversions: 'Konvertierungen nicht verfügbar',
      ratesUnavailable: 'Kurse nicht verfügbar'
    },
    es: {
      trigger: 'Precio en otras monedas',
      title: 'Precio en otras monedas',
      subtitle: 'Los tipos mostrados reflejan el cambio actual y pueden variar.',
      loading: 'Cargando tipos…',
      note: 'Precios orientativos. Cobro in EUR en el pago.',
      close: 'Cerrar',
      noConversions: 'Conversiones no disponibles',
      ratesUnavailable: 'Tipos no disponibles'
    }
  };

  // Rilevamento lingua/locale
  const detectLocale = () => {
    const lang = (win.Shopify && win.Shopify.locale) || (doc.documentElement && doc.documentElement.lang) || navigator.language;
    const s = (lang || '').toString().toLowerCase();
    if (s.startsWith('it')) return 'it';
    if (s.startsWith('fr')) return 'fr';
    if (s.startsWith('de')) return 'de';
    if (s.startsWith('es')) return 'es';
    return 'en';
  };

  const LOCALE = detectLocale();
  const T = TRANSLATIONS[LOCALE] || TRANSLATIONS.en;

  // CSS iniettato dinamicamente
  const CSS = `#${CONFIG.BTN_ID}{position:absolute;top:0;left:0;z-index:${CONFIG.BTN_Z_INDEX};display:inline-flex;align-items:center;justify-content:center;padding:6px 10px;background:#ffffff;color:#2c2c2c;border-radius:5px;border:1px solid #dddddd;cursor:pointer;font-size:12px;line-height:1;white-space:nowrap;transform:translate3d(-9999px,-9999px,0);opacity:0;pointer-events:none;transition:opacity .16s ease-out,transform .16s ease-out;box-sizing:border-box}#${CONFIG.BTN_ID}.visible{opacity:1;pointer-events:auto}#${CONFIG.BTN_ID}:hover{background:#F5F3EF;color:#2c2c2c;border-color:#dddddd}#${CONFIG.BACKDROP_ID}{position:fixed;inset:0;background:rgba(0,0,0,.36);display:none;align-items:center;justify-content:center;z-index:100000}#${CONFIG.MODAL_ID}{width:min(720px,92vw);max-height:92vh;overflow:auto;background:#fff;color:#2c2c2c;border-radius:10px;padding:18px;box-shadow:0 18px 50px rgba(0,0,0,.35);box-sizing:border-box;position:relative;outline:none}.cc-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 0 8px 0}.cc-title{font-size:20px;font-weight:700;margin:0;line-height:1.05}.cc-subtitle{display:block;width:100%;font-size:13px;margin:12px 0}.cc-list{margin:10px 0;padding:0;list-style:none;max-height:320px;overflow:auto}.cc-list li{display:flex;justify-content:space-between;padding:8px 6px;border-bottom:1px solid #f2f2f2;font-size:14px}.cc-note{margin-top:12px;font-size:13px}.cc-close-btn{background:transparent;border:none;padding:0;margin:0;font-size:16px;font-weight:bold;cursor:pointer}@media(max-width:480px){#${CONFIG.MODAL_ID}{padding:16px;width:92%}.cc-title{font-size:18px}}`;

  const injectStyle = () => {
    if (doc.getElementById(CONFIG.STYLE_ID)) return;
    const s = doc.createElement('style');
    s.id = CONFIG.STYLE_ID;
    s.appendChild(doc.createTextNode(CSS));
    (doc.head || doc.documentElement).appendChild(s);
  };

  injectStyle();

  // Cache per formatter Intl.NumberFormat
  const formatterCache = Object.create(null);
  const getFormatter = (currency) => {
    if (!formatterCache[currency]) {
      try {
        formatterCache[currency] = new Intl.NumberFormat(LOCALE, { style: 'currency', currency });
      } catch (e) {
        formatterCache[currency] = null;
      }
    }
    return formatterCache[currency];
  };

  const formatCurrency = (v, c) => {
    const f = getFormatter(c);
    if (f) return f.format(v);
    return Number(v).toFixed(2) + ' ' + c;
  };

  // Parsing robusto del prezzo in EUR da testo (gestisce formati europeo/anglosassone)
  function parseEurFromText(text) {
    if (!text) return null;
    const cleaned = String(text).trim().replace(/[^\d\-\+.,]/g, '').trim();
    if (!cleaned) return null;
    const reEuropean = /^[-+]?\d{1,3}(?:\.\d{3})*(?:,\d+)?$/;
    const reAnglo = /^[-+]?\d{1,3}(?:,\d{3})*(?:\.\d+)?$/;
    const reSimple = /^[-+]?\d+(?:[.,]\d+)?$/;
    if (reEuropean.test(cleaned)) {
      const normalized = cleaned.replace(/\./g, '').replace(',', '.');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : null;
    }
    if (reAnglo.test(cleaned)) {
      const normalized = cleaned.replace(/,/g, '');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : null;
    }
    if (reSimple.test(cleaned)) {
      const normalized = cleaned.replace(',', '.');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  // IntersectionObserver per visibilità dei nodi prezzo
  let io = null;
  const visibleCache = new WeakMap();
  const observeVisibility = (node) => {
    if (!node) return;
    if ('IntersectionObserver' in win) {
      if (!io) {
        io = new IntersectionObserver((entries) => {
          entries.forEach((e) => visibleCache.set(e.target, e.isIntersecting && e.intersectionRatio > 0));
        }, { threshold: [0, 0.01] });
      }
      try { io.observe(node); } catch (e) { visibleCache.set(node, node.offsetParent !== null); }
    } else {
      visibleCache.set(node, node.offsetParent !== null);
    }
  };

  const isVisible = (el) => {
    if (!el) return false;
    if (visibleCache.has(el)) return !!visibleCache.get(el);
    observeVisibility(el);
    return el.offsetParent !== null;
  };

  // Utility sleep
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Fetch con retry, backoff e AbortController
  const fetchRetry = async (url, opts = {}, timeout = 5000, attempts = 3, baseDelay = 300) => {
    const supportsAbort = typeof AbortController !== 'undefined';
    let lastErr = null;
    for (let i = 0; i < attempts; i++) {
      let controller, id;
      try {
        if (supportsAbort) {
          controller = new AbortController();
          id = setTimeout(() => controller.abort(), timeout);
        }
        const res = await fetch(url, Object.assign({}, opts, supportsAbort ? { signal: controller.signal } : {}));
        if (supportsAbort && id) clearTimeout(id);
        if (!res.ok) throw new Error('Network response not ok: ' + res.status);
        return res;
      } catch (err) {
        if (supportsAbort && id) clearTimeout(id);
        lastErr = err;
        if (i < attempts - 1) {
          const backoff = Math.min(2000, Math.round(baseDelay * Math.pow(2, i)) + Math.random() * baseDelay);
          await sleep(backoff);
        }
      }
    }
    throw lastErr;
  };

  // Cache dei tassi con TTL e gestione failure window
  const rateCache = { ts: 0, ttl: CONFIG.RATE_TTL_MS, rates: null, failures: 0, failureWindowTs: 0 };

  // Hook di report (se definito dall'integrazione esterna)
  const report = (event, payload) => {
    if (typeof win.__cc_report === 'function') {
      try { win.__cc_report(event, payload); } catch (e) {}
    }
  };

  // Recupero tassi con fallback su più endpoint
  const fetchRatesFallback = async () => {
    const now = Date.now();
    if (rateCache.rates && now - rateCache.ts < rateCache.ttl) return rateCache.rates;
    if (rateCache.failures >= 3 && now - rateCache.failureWindowTs < 10 * 60 * 1000) {
      rateCache.ts = now;
      rateCache.ttl = Math.min(CONFIG.RATE_TTL_MS * 4, 60 * 60 * 1000);
      return rateCache.rates || {};
    }
    for (let url of CONFIG.RATE_ENDPOINTS) {
      try {
        const res = await fetchRetry(url, { cache: 'no-store' }, 6000, 2, 300);
        const data = await res.json();
        if (data && data.rates) {
          const rates = Object.create(null);
          CONFIG.CURRENCIES.forEach((k) => {
            if (k !== 'EUR' && data.rates[k] !== undefined) rates[k] = data.rates[k];
          });
          rateCache.rates = rates;
          rateCache.ts = Date.now();
          rateCache.failures = 0;
          rateCache.failureWindowTs = 0;
          rateCache.ttl = CONFIG.RATE_TTL_MS;
          report('rates_fetch_success', { url });
          return rates;
        }
      } catch (e) {
        rateCache.failures = (rateCache.failures || 0) + 1;
        rateCache.failureWindowTs = rateCache.failureWindowTs || Date.now();
        report('rates_fetch_error', { url, message: (e && e.message) || String(e) });
      }
    }
    return rateCache.rates || {};
  };

  // Creazione o recupero del pulsante flottante
  const createButton = (parent) => {
    let b = doc.getElementById(CONFIG.BTN_ID);
    if (b) {
      if (parent && b.parentElement !== parent) parent.appendChild(b);
      b.textContent = T.trigger;
      return b;
    }
    b = doc.createElement('button');
    b.id = CONFIG.BTN_ID;
    b.type = 'button';
    b.setAttribute('aria-label', `${T.trigger} — ${T.subtitle}`);
    b.setAttribute('aria-expanded', 'false');
    b.setAttribute('aria-disabled', 'true');
    b.textContent = T.trigger;
    (parent || doc.body).appendChild(b);
    return b;
  };

  // Creazione della modale e backdrop
  let backdropClickHandler = null;
  let globalKeydownHandler = null;
  const createModal = () => {
    let backdrop = doc.getElementById(CONFIG.BACKDROP_ID);
    let modal = doc.getElementById(CONFIG.MODAL_ID);
    if (backdrop && modal) return { backdrop, modal };

    backdrop = doc.createElement('div');
    backdrop.id = CONFIG.BACKDROP_ID;

    modal = doc.createElement('div');
    modal.id = CONFIG.MODAL_ID;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('aria-labelledby', `${CONFIG.MODAL_ID}-title`);

    const header = doc.createElement('div');
    header.className = 'cc-header';

    const title = doc.createElement('h2');
    title.id = `${CONFIG.MODAL_ID}-title`;
    title.className = 'cc-title';
    title.textContent = T.title;

    const closeBtn = doc.createElement('button');
    closeBtn.className = 'cc-close-btn';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', T.close || 'Close');
    closeBtn.textContent = '✖';
    closeBtn.addEventListener('click', () => closeModal(backdrop), false);

    header.appendChild(title);
    header.appendChild(closeBtn);

    const subtitle = doc.createElement('div');
    subtitle.className = 'cc-subtitle';
    subtitle.textContent = T.subtitle;

    const body = doc.createElement('div');

    const loading = doc.createElement('div');
    loading.className = 'cc-loading';
    loading.textContent = T.loading;
    loading.style.opacity = '0.85';

    const list = doc.createElement('ul');
    list.className = 'cc-list';

    const note = doc.createElement('div');
    note.className = 'cc-note';
    note.textContent = T.note;

    modal.appendChild(header);
    modal.appendChild(subtitle);
    body.appendChild(loading);
    body.appendChild(list);
    body.appendChild(note);
    modal.appendChild(body);
    backdrop.appendChild(modal);
    doc.body.appendChild(backdrop);

    backdropClickHandler = (e) => { if (e.target === backdrop) closeModal(backdrop); };
    backdrop.addEventListener('click', backdropClickHandler, { passive: true });

    globalKeydownHandler = (e) => { if (e.key === 'Escape') closeModal(backdrop); };
    doc.addEventListener('keydown', globalKeydownHandler, false);

    return { backdrop, modal };
  };

  // Gestione focus trap nella modale
  let lastFocusedElement = null;
  const focusableSelector = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  let activeFocusTrap = null;

  const activateFocusTrap = (modal) => {
    if (!modal) return;
    const focusables = Array.from(modal.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
    if (focusables.length === 0) { modal.focus(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (doc.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (doc.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    modal.addEventListener('keydown', trapHandler, false);
    activeFocusTrap = () => { modal.removeEventListener('keydown', trapHandler, false); activeFocusTrap = null; };
    try { first.focus(); } catch (e) { modal.focus(); }
  };

  const deactivateFocusTrap = () => { if (typeof activeFocusTrap === 'function') activeFocusTrap(); activeFocusTrap = null; };

  // Chiusura modale e ripristino stato
  const closeModal = (backdrop) => {
    if (!backdrop) return;
    backdrop.style.display = 'none';
    const b = doc.getElementById(CONFIG.BTN_ID);
    if (b) { b.classList.remove('visible'); try { b.setAttribute('aria-expanded', 'false'); } catch (e) {} }
    deactivateFocusTrap();
    if (lastFocusedElement && lastFocusedElement.focus) { try { lastFocusedElement.focus(); } catch (e) {} }
    lastFocusedElement = null;
    if (win.__cc_currency && typeof win.__cc_currency.forceReposition === 'function') {
      try { win.__cc_currency.forceReposition(); } catch (e) {}
    }
  };

  // Costruzione lista conversioni usando DocumentFragment
  const buildListFromRatesInto = (container, rates, eurAmount) => {
    while (container.firstChild) container.removeChild(container.firstChild);
    const frag = doc.createDocumentFragment();
    CONFIG.CURRENCIES.forEach((cur) => {
      const li = doc.createElement('li');
      const left = doc.createElement('span');
      left.textContent = cur;
      const right = doc.createElement('span');
      if (cur === 'EUR') right.textContent = formatCurrency(eurAmount, 'EUR');
      else {
        const rate = rates[cur];
        right.textContent = Number.isFinite(rate) && rate > 0 ? formatCurrency(eurAmount * rate, cur) : '—';
      }
      li.appendChild(left);
      li.appendChild(right);
      frag.appendChild(li);
    });
    container.appendChild(frag);
  };

  // Mostra la modale con le conversioni calcolate
  const showConversions = async (modalElements, listContainer, priceNode) => {
    const { backdrop, modal } = modalElements;
    const loadingEl = modal.querySelector('.cc-loading');
    if (loadingEl) loadingEl.textContent = T.loading;
    while (listContainer.firstChild) listContainer.removeChild(listContainer.firstChild);
    backdrop.style.display = 'flex';
    const text = (priceNode && priceNode.textContent) || '';
    const eurAmount = parseEurFromText(text);
    if (!Number.isFinite(eurAmount)) { if (loadingEl) loadingEl.textContent = T.noConversions || T.ratesUnavailable; return; }
    const rates = await fetchRatesFallback();
    if (!rates || Object.keys(rates).length === 0) { if (loadingEl) loadingEl.textContent = T.noConversions || T.ratesUnavailable; return; }
    if (loadingEl) loadingEl.textContent = '';
    buildListFromRatesInto(listContainer, rates, eurAmount);
    activateFocusTrap(modal);
  };

  // Posizionamento del pulsante rispetto al nodo prezzo usando translate3d (evita reflow)
  const positionButtonRelativeTo = (node, btnEl, containerRect) => {
    if (!node || !btnEl || !containerRect) return;
    const nodeRect = node.getBoundingClientRect();
    const btnHeight = btnEl.offsetHeight || 32;
    const desiredLeft = Math.round(nodeRect.left + nodeRect.width - containerRect.left + CONFIG.OFFSET_X);
    const priceCenterY = nodeRect.top - containerRect.top + nodeRect.height / 2;
    const desiredTop = Math.round(priceCenterY - btnHeight / 2);
    const clampedLeft = Math.max(8, desiredLeft);
    const prevTransition = btnEl.style.transition;
    btnEl.style.transition = 'none';
    btnEl.style.transform = `translate3d(${clampedLeft}px, ${desiredTop}px, 0)`;
    btnEl.classList.add('visible');
    requestAnimationFrame(() => { btnEl.style.transition = prevTransition || ''; });
  };

  // Factory per riposizionamento stabile con cache del bounding rect del container
  const stableRepositionFactory = (nodeGetter, btnEl, containerEl) => {
    let t = null;
    let cachedContainerRect = null;
    let cacheTs = 0;
    const CACHE_TTL = CONFIG.CONTAINER_RECT_CACHE_TTL;
    const invalidate = () => { cachedContainerRect = null; cacheTs = 0; };
    if (!win.__cc_currency) win.__cc_currency = {};
    win.__cc_currency.invalidateContainerRect = invalidate;
    return () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const node = nodeGetter();
        if (!node) return;
        const now = Date.now();
        if (!cachedContainerRect || now - cacheTs > CACHE_TTL) {
          try { cachedContainerRect = containerEl.getBoundingClientRect(); cacheTs = now; } catch (e) { cachedContainerRect = null; }
        }
        positionButtonRelativeTo(node, btnEl, cachedContainerRect);
        setTimeout(() => { cachedContainerRect = null; cacheTs = 0; }, 250);
      }, 80);
    };
  };

  // Stato iniziale e variabili globali per lo script
  let initialized = false;
  let observer = null;
  let btnClickHandler = null;
  let resizeObserver = null;
  let cachedPriceNodes = [];

  // Pulizia completa: rimuove listener, osservatori e elementi DOM creati
  const teardown = () => {
    if (observer && typeof observer.disconnect === 'function') observer.disconnect();
    observer = null;
    if (io && typeof io.disconnect === 'function') io.disconnect();
    io = null;
    if (globalKeydownHandler) doc.removeEventListener('keydown', globalKeydownHandler, false);
    globalKeydownHandler = null;
    const backdrop = doc.getElementById(CONFIG.BACKDROP_ID);
    if (backdrop && backdropClickHandler) backdrop.removeEventListener('click', backdropClickHandler, { passive: true });
    backdropClickHandler = null;
    const b = doc.getElementById(CONFIG.BTN_ID);
    if (b) {
      if (btnClickHandler) b.removeEventListener('click', btnClickHandler, false);
      try { b.removeAttribute('data-cc-listener-attached'); } catch (e) {}
      if (b.parentElement) b.parentElement.removeChild(b);
    }
    btnClickHandler = null;
    if (backdrop && backdrop.parentElement) backdrop.parentElement.removeChild(backdrop);
    if (win.__cc_currency) {
      if (win.__cc_currency.resizeHandler) win.removeEventListener('resize', win.__cc_currency.resizeHandler, { passive: true });
      if (win.__cc_currency.scrollHandler) win.removeEventListener('scroll', win.__cc_currency.scrollHandler, { passive: true });
      try { delete win.__cc_currency.invalidateContainerRect; } catch (e) {}
      try { delete win.__cc_currency.forceReposition; } catch (e) {}
      try { delete win.__cc_currency.cleanup; } catch (e) {}
    }
    win.removeEventListener('beforeunload', teardown, { passive: true });
    if (resizeObserver && typeof resizeObserver.disconnect === 'function') resizeObserver.disconnect();
    resizeObserver = null;
    cachedPriceNodes.forEach((n) => {
      if (io && typeof io.unobserve === 'function') try { io.unobserve(n); } catch (e) {}
      if (resizeObserver && typeof resizeObserver.unobserve === 'function') try { resizeObserver.unobserve(n); } catch (e) {}
    });
    cachedPriceNodes = [];
    if (activeFocusTrap) deactivateFocusTrap();
    initialized = false;
  };

  // Esegue una funzione in idle (requestIdleCallback o setTimeout fallback)
  const idle = (fn, timeout = 2000) => {
    if ('requestIdleCallback' in win) {
      try { win.requestIdleCallback(fn, { timeout }); return; } catch (e) {}
    }
    setTimeout(fn, 200);
  };

  let lastQueryAt = 0;
  const MIN_QUERY_INTERVAL = 500;

  // Inizializzazione principale
  const init = () => {
    if (initialized) return;
    initialized = true;

    let priceContainer = doc.querySelector(GENERIC_PRICE_CONTAINER_SELECTOR) || doc.body;
    if (priceContainer && priceContainer !== doc.body) {
      try {
        if (priceContainer.getBoundingClientRect().width < 200 && priceContainer.parentElement) priceContainer = priceContainer.parentElement;
        const cs = getComputedStyle(priceContainer);
        if (cs.position === 'static') priceContainer.style.position = 'relative';
        if (cs.overflow === 'hidden' || cs.overflowX === 'hidden' || cs.overflowY === 'hidden') priceContainer.style.overflow = 'visible';
      } catch (e) {}
    }

    const btn = createButton(priceContainer);
    const modalElements = createModal();
    const listContainer = modalElements.modal.querySelector('.cc-list');
    const searchScope = (priceContainer && priceContainer !== doc.body) ? priceContainer : doc;

    cachedPriceNodes = Array.from(searchScope.querySelectorAll(PRICE_SELECTOR));
    lastQueryAt = Date.now();
    cachedPriceNodes.forEach(observeVisibility);

    let currentPriceNode = null;
    let rafPending = false;

    const scheduleSetup = (fn) => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => { rafPending = false; fn(); });
    };

    const getCurrentPriceNode = () => currentPriceNode;
    const stableReposition = stableRepositionFactory(getCurrentPriceNode, btn, priceContainer);

    const enableButton = () => { try { btn.removeAttribute('aria-disabled'); } catch (e) {} try { btn.setAttribute('aria-disabled', 'false'); } catch (e) {} };
    const disableButton = (reason) => { try { btn.setAttribute('aria-disabled', 'true'); } catch (e) {} try { btn.setAttribute('title', reason || ''); } catch (e) {} btn.classList.remove('visible'); };

    const findFirstValidPriceNode = () => {
      if (!cachedPriceNodes || !cachedPriceNodes.length) return null;
      for (let i = 0; i < cachedPriceNodes.length; i++) {
        const n = cachedPriceNodes[i];
        if (!n) continue;
        if (!isVisible(n)) continue;
        const val = parseEurFromText(n.textContent || '');
        if (Number.isFinite(val)) return n;
      }
      return null;
    };

    const setup = () => {
      const priceNode = findFirstValidPriceNode() || searchScope.querySelector(PRICE_SELECTOR);
      if (!priceNode || !isVisible(priceNode) || !Number.isFinite(parseEurFromText(priceNode.textContent || ''))) {
        disableButton(T.ratesUnavailable);
        return;
      }
      currentPriceNode = priceNode;
      stableReposition();
      enableButton();
      if (!btn.dataset.ccListenerAttached) {
        btnClickHandler = () => {
          try { btn.setAttribute('aria-expanded', 'true'); } catch (e) {}
          lastFocusedElement = doc.activeElement;
          showConversions(modalElements, listContainer, currentPriceNode).catch(() => {});
        };
        btn.addEventListener('click', btnClickHandler, false);
        btn.dataset.ccListenerAttached = '1';
      }
    };

    const debouncedObserverCallback = (() => {
      let t = null;
      return () => {
        clearTimeout(t);
        t = setTimeout(() => {
          const now = Date.now();
          if (now - lastQueryAt < MIN_QUERY_INTERVAL) { setup(); return; }
          cachedPriceNodes = Array.from(searchScope.querySelectorAll(PRICE_SELECTOR));
          lastQueryAt = now;
          cachedPriceNodes.forEach(observeVisibility);
          if (win.__cc_currency && typeof win.__cc_currency.invalidateContainerRect === 'function') {
            try { win.__cc_currency.invalidateContainerRect(); } catch (e) {}
          }
          setup();
        }, 200);
      };
    })();

    try {
      observer = new MutationObserver(debouncedObserverCallback);
      const observeTarget = (searchScope && searchScope !== doc) ? searchScope : doc.body;
      observer.observe(observeTarget, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-price'] });
    } catch (e) {
      observer = null;
    }

    win.__cc_currency = win.__cc_currency || {};
    win.__cc_currency.rqf = false;

    const throttledScrollResize = () => {
      if (win.__cc_currency.rqf) return;
      win.__cc_currency.rqf = true;
      requestAnimationFrame(() => {
        scheduleSetup(setup);
        if (win.__cc_currency && typeof win.__cc_currency.invalidateContainerRect === 'function') {
          try { win.__cc_currency.invalidateContainerRect(); } catch (e) {}
        }
        win.__cc_currency.rqf = false;
      });
    };

    win.__cc_currency.resizeHandler = throttledScrollResize;
    win.__cc_currency.scrollHandler = throttledScrollResize;
    win.addEventListener('resize', win.__cc_currency.resizeHandler, { passive: true });
    win.addEventListener('scroll', win.__cc_currency.scrollHandler, { passive: true });
    win.addEventListener('beforeunload', teardown, { passive: true });

    if ('ResizeObserver' in win) {
      let lastResizeAt = 0;
      try {
        resizeObserver = new ResizeObserver(() => {
          const now = Date.now();
          if (now - lastResizeAt < CONFIG.MIN_RESIZE_INTERVAL) return;
          lastResizeAt = now;
          scheduleSetup(setup);
        });
        resizeObserver.observe(priceContainer);
      } catch (e) {
        resizeObserver = null;
      }
    }

    const attachPriceNodeObserver = () => {
      if (resizeObserver && currentPriceNode) {
        try { resizeObserver.unobserve(currentPriceNode); } catch (e) {}
        try { resizeObserver.observe(currentPriceNode); } catch (e) {}
      }
    };

    idle(async () => {
      try {
        const rates = await fetchRatesFallback();
        if (!rates || Object.keys(rates).length === 0) disableButton(T.ratesUnavailable);
        else enableButton();
      } catch (e) { disableButton(T.ratesUnavailable); }
    }, 1500);

    idle(() => { attachPriceNodeObserver(); }, 1000);

    setup();

    Object.defineProperty(win.__cc_currency, 'forceReposition', { configurable: true, enumerable: false, writable: false, value: () => scheduleSetup(setup) });
    Object.defineProperty(win.__cc_currency, 'cleanup', { configurable: true, enumerable: false, writable: false, value: teardown });

    const spaHandler = () => {
      teardown();
      setTimeout(() => { try { init(); } catch (e) {} }, 50);
    };

    try { doc.addEventListener('shopify:section:load', spaHandler, false); } catch (e) {}
    try { doc.addEventListener('page:change', spaHandler, false); } catch (e) {}
    try { win.addEventListener('popstate', spaHandler, false); } catch (e) {}
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

})();
